import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CHAT_ANIMATIONS } from '@/utils/chat-animations';
import { FileText, Plus, Check, AlertCircle, Loader2, PenTool } from 'lucide-react';
import { Button } from '@/components/ui/button';
import GlassPill from '@/components/ui/GlassPill';
import { useAgentChatPageCreationWithTracking } from '@/hooks/ai-agents/functions/core/useAgentChatPageCreationWithTracking';
import { useRecommendationCardState, RECOMMENDATION_CONFIGS } from '@/hooks/ai-agents/functions/core/createRecommendationTrackingWrapper';
import { useRecommendationTracking } from '@/hooks/ai-agents/functions/core/useRecommendationTracking';
import { useThreadContext } from '@/hooks/ai-agents/functions/core/useThreadContext';
import { useNavigate } from 'react-router-dom';
import { 
  getProjectInfoById,
  getProjectNavigationUrl,
  getGoalNavigationUrl,
  useNavigationWithModalClose
} from '@/hooks/ai-agents/functions/core/useResponseParsing';
import { cn } from '@/lib/utils';
import { ExtractedPage } from '@/hooks/ai-agents/functions/core/useRecommendationDetection';
import { MessageDisplay } from '@/hooks/ai-agents/types/chat-hook-types';
import { 
  getCardContainerClasses, 
  getCardIconClasses, 
  getCardButtonClasses 
} from './shared/RecommendationCardStyles';

interface ChatRecommendedPageCardWithContentProps {
  page: ExtractedPage;
  threadId: string;
  message: MessageDisplay;
  type?: 'page';
  className?: string;
}

type SaveState = 'idle' | 'saving' | 'success' | 'saved' | 'error';

/**
 * Chat Recommended Page Card With Content Generation - Individual page item with generate content button
 * Shows page recommendations with option to save to project workbook AND open AI Text Magic modal
 * Identical to ChatRecommendedPageCard but navigates with content generation parameter
 */
export const ChatRecommendedPageCardWithContent: React.FC<ChatRecommendedPageCardWithContentProps> = ({
  page,
  threadId,
  message,
  type = 'page',
  className
}) => {
  const { navigateAndCloseModal } = useNavigationWithModalClose();
  
  // Get thread context to find the workbook ID
  const { data: threadContext } = useThreadContext(threadId);
  
  // Use standardized tracking state management with full message object
  const { localState, setLocalState } = useRecommendationCardState(message, 'page');
  
  // Page creation with tracking using full message object
  const { mutateWithTracking, isGenerating } = useAgentChatPageCreationWithTracking(message);
  
  // Get saved recommendation data for view functionality using full message object
  const { isRecommendationSaved } = useRecommendationTracking(message.id, message.databaseId);
  
  // Handle viewing the saved page with content generation
  const handleViewPageWithContent = async () => {
    const status = await isRecommendationSaved('page');
    if (status.isSaved && status.savedItemId && status.metadata) {
      const { workbookId } = status.metadata;
      const pageId = status.savedItemId;
      
      if (workbookId && pageId) {
        console.log('[ChatRecommendedPageCardWithContent] Navigating to page with content generation:', { pageId, workbookId });
        
        // Navigate to new project page editor route (content generation handled by page editor)
        navigateAndCloseModal(`/project/${workbookId}/page/${pageId}`);
      }
    } else {
      console.error('[ChatRecommendedPageCardWithContent] No saved page data found');
    }
  };
  
  // Get config for this recommendation type
  const config = RECOMMENDATION_CONFIGS.page;
  
  // Don't render if no thread context (no project to attach to)
  if (!threadContext?.hasProjectContext) {
    return null;
  }

  // Get project and goal information for navigation
  const projectInfo = threadContext?.workbookId ? getProjectInfoById(threadContext.workbookId) : null;

  // Handle saving this specific page (same as original)
  const handleSavePage = async () => {
    if (!threadContext?.workbookId) return;
    
    setLocalState('saving');
    
    try {
      const result = await mutateWithTracking({
        page,
        workbookId: threadContext.workbookId,
        goalThreadId: threadContext.goalId, // Use goalId as thread context
        messageId: message.id, // Use message.id for consistency
      });
      
      setLocalState('saved');
      
    } catch (error) {
      console.error('[ChatRecommendedPageCardWithContent] Failed to save page:', error);
      setLocalState('error');
      
      // Reset to idle after 3 seconds
      setTimeout(() => {
        setLocalState('idle');
      }, 3000);
    }
  };

  // Get button content based on state
  const getButtonContent = () => {
    switch (localState) {
      case 'saving':
        return <Loader2 size={12} className="animate-spin" />;
      case 'success':
        return <Check size={12} />;
      case 'saved':
        return <PenTool size={12} />; // Different icon for content generation
      case 'error':
        return <AlertCircle size={12} />;
      default:
        return <Plus size={12} />;
    }
  };

  // Get button text based on state - customized for content generation
  const getButtonText = () => {
    switch (localState) {
      case 'saving':
        return 'Creating...';
      case 'success':
        return 'Page Created!';
      case 'saved':
        return 'Generate Content';
      case 'error':
        return 'Try Again';
      default:
        return 'Create & Generate';
    }
  };

  // Get button click handler based on state
  const getButtonHandler = () => {
    switch (localState) {
      case 'saved':
        return handleViewPageWithContent;
      default:
        return handleSavePage;
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        variants={CHAT_ANIMATIONS.cardEntry}
        initial="initial"
        animate="animate"
        exit="exit"
        className={cn("mt-3 w-full", className)}
      >
        <div className={cn(getCardContainerClasses(type))}>
          <div className="flex items-start gap-2.5">
            {/* Save Button - Left Side */}
            <div className="flex-shrink-0 pt-0.5">
              <Button
                size="sm"
                variant="outline"
                onClick={getButtonHandler()}
                disabled={localState === 'saving' || localState === 'success'}
                className={cn(
                  "h-7 px-3 text-xs border-purple-300 dark:border-purple-600",
                  "hover:bg-purple-50 dark:hover:bg-purple-900/20",
                  "transition-all duration-200",
                  // State-specific styling
                  localState === 'success' && 'border-green-500 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400',
                  localState === 'saved' && 'border-purple-400 bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300',
                  localState === 'error' && 'border-red-500 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400',
                )}
              >
                <span className="flex items-center gap-1.5">
                  {getButtonContent()}
                  <span>{getButtonText()}</span>
                </span>
              </Button>
            </div>
            
            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-sm text-gray-700 dark:text-gray-200 block font-medium">
                  {page.title}
                </span>
                <GlassPill type={page.type} variant="purple" />
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2">
                {page.description}
              </p>
              
              {/* Project and Goal Navigation Pills */}
              {projectInfo && (
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  <div 
                    onClick={() => navigateAndCloseModal(getProjectNavigationUrl(threadContext?.workbookId || ''))}
                    className="cursor-pointer hover:opacity-80 transition-opacity"
                  >
                    <GlassPill 
                      type={projectInfo.title} 
                      variant="purple"
                    />
                  </div>
                  <div 
                    onClick={() => navigateAndCloseModal(getGoalNavigationUrl(projectInfo.goalId))}
                    className="cursor-pointer hover:opacity-80 transition-opacity"
                  >
                    <GlassPill 
                      type={projectInfo.goalTitle} 
                      variant="purple"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}; 